import { mountAgentWidget } from "./chatbot";
import { getCurrentScript, readConfig } from "./config";
import { resolveAdapter } from "./platform";
import { fetchRemoteConfig } from "./remoteConfig";
import { createRouteHandler, runWhenIdle } from "./route-handler";
import { bindClickTracking, createTracker } from "./tracking";
import { getOrCreateVisitorId } from "./visitor-id";

function afterLoad(fn: () => void): void {
  if (document.readyState === "complete") fn();
  else window.addEventListener("load", fn, { once: true });
}

async function run(): Promise<void> {
  const localConfig = readConfig(getCurrentScript());
  if (!localConfig.publicKey) return;

  // A config remota controla on/off de features e branding a partir do backend.
  // Plataforma fica sempre por conta do data-platform/auto-detecção local: a mesma
  // chave pública pode estar instalada em lojas de plataformas diferentes.
  const remoteConfig = await fetchRemoteConfig(localConfig.apiBase, localConfig.publicKey);
  const features = remoteConfig?.features ?? localConfig.features;
  // const branding = remoteConfig?.branding ?? null;

  const resolved = resolveAdapter(localConfig.platform);
  const platform = resolved?.platform ?? "unknown";

  const tracker = createTracker(localConfig.apiBase, localConfig.publicKey, platform);

  if (features.tracking) bindClickTracking(tracker);

  const route = createRouteHandler({
    adapter: resolved?.adapter ?? null,
    tracker,
    features,
    apiBase: localConfig.apiBase,
    publicKey: localConfig.publicKey,
    connectionId: localConfig.connectionId,
  });

  // Dois modos no mesmo handler: páginas completas (Shopware, um load por produto) e SPA
  // (VTEX/Tray, pushState). A carga inicial não pode sumir: foi comentada em 77e87e7 (25/08)
  // junto com o widget, e desde então o primeiro produto da visita não gera eventos nem schema.
  afterLoad(() => runWhenIdle(() => route.start()));

  // features.chatbot da config remota é ignorado de propósito: o backend força false.
  // O assistente liga/desliga por assistant != null; o kill switch local (data-chatbot) vale.
  const assistant = remoteConfig?.assistant ?? null;
  if (assistant && localConfig.features.chatbot) {
    const visitorId = getOrCreateVisitorId();
    if (visitorId) {
      // Montagem única: mountAgentWidget é idempotente (checa o id do root), mas não há motivo
      // pra chamar de novo a cada navegação. getProduct é lido só no envio, não aqui.
      mountAgentWidget({
        apiBase: localConfig.apiBase,
        publicKey: localConfig.publicKey,
        assistant,
        tracker,
        getProduct: () => route.getProduct(),
        visitorId,
      });
    }
  }
}

function init(): void {
  if (typeof document === "undefined") return;

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => void run());
  } else {
    void run();
  }
}

init();
